import { useCallback, useEffect, useReducer, useState } from 'react';
import { fetchBusinessClients } from '~/api';
import { useBoolean } from '~/hooks/useBoolean';
import { useUserLimits } from '~/hooks/useUserLimits';
import { useSelector } from '~/store';
import { selectBusinessUuid } from '~/store/slices/formSlice/selectors';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';
import { IListClient } from '../constants';

interface IBusinessClientApiResponse {
  next: string | null;
  previous: string | null;
  count: number;
  total_pages: number;
  results: IListClient[];
}

interface State {
  listIds: string[];
  listData: Record<string, IListClient>;
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalCount: number;
}

// const TEMP_CLIENT : IListClient = {
//   id: 0,
//   business_client_uuid: "111",
//   company_name: "string;",
//   email: "string;",
//   country_code: "string;",
//   phone: "string;",
//   currency: "string;",
//   total_billed: 0,
// }

const initialState: State = {
  listIds: [],
  listData: {},
  isLoading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
};

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: { clients: IBusinessClientApiResponse; page: number } }
  | { type: 'FETCH_FAILURE'; error: string }
  | { type: 'RESET_LIST' };

const clientReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null };

    case 'FETCH_SUCCESS':
      const { clients, page } = action.payload;
      const newIds = clients.results.map(client => client.business_client_uuid);
      const newData = clients.results.reduce<Record<string, IListClient>>((acc, client) => {
        acc[client.business_client_uuid] = client;
        return acc;
      }, {});

      return {
        ...state,
        isLoading: false,
        listIds: newIds,
        listData: newData,
        currentPage: page,
        totalPages: clients.total_pages,
        totalCount: clients.count,
      };

    case 'FETCH_FAILURE':
      return { ...state, isLoading: false, error: action.error };

    case 'RESET_LIST':
      return { ...initialState };

    default:
      return state;
  }
};

export const useClientList = (shouldFetch = true) => {
  const [state, dispatch] = useReducer(clientReducer, initialState);
  const [clientId, setClientId] = useState<null | string>(null);
  const [isModalOpen, modalActions] = useBoolean();
  const [deleteClientId, setDeleteClientId] = useState<null | string>(null);
  const businessUuid = useSelector(selectBusinessUuid);
  
  // Use user limits hook - only fetch if shouldFetch is true
  const { canCreateClient } = useUserLimits(shouldFetch);

  const fetchClients = useCallback(async (page = 1) => {
    dispatch({ type: 'FETCH_START' });

    const promise = fetchBusinessClients(businessUuid, page);
    const response = await apiResponseHandler<IBusinessClientApiResponse>(promise);

    if (response.type === ApiResponseEnum.Success) {
      dispatch({ type: 'FETCH_SUCCESS', payload: { clients: response.data, page } });
    } else {
      dispatch({ type: 'FETCH_FAILURE', error: response.error });
    }
  }, [businessUuid]);

  useEffect(() => {
    if (shouldFetch) {
      fetchClients(state.currentPage);
    }
  }, [shouldFetch, businessUuid, isModalOpen, deleteClientId]);

  return {
    ...state,
    clientId,
    setClientId,
    deleteClientId,
    setDeleteClientId,
    isModalOpen,
    modalActions,
    canCreateClient,
    fetchNextPage: () => {
      if (state.currentPage < state.totalPages) {
        fetchClients(state.currentPage + 1);
      }
    },
    fetchSpecificPage: (page: number) => {
      if (page >= 1 && page <= state.totalPages) {
        fetchClients(page);
      }
    },
    resetList: () => dispatch({ type: 'RESET_LIST' }),
  };
};
